import { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Gift } from 'lucide-react';
import { isDisposableEmail, DISPOSABLE_EMAIL_ERROR } from '@/utils/disposableEmailDomains';

const isNative = Capacitor.isNativePlatform();

const getPasswordStrength = (pwd: string) => {
  const checks = {
    minLength: pwd.length >= 6,
    hasUppercase: /[A-Z]/.test(pwd),
    hasLowercase: /[a-z]/.test(pwd),
    hasNumber: /[0-9]/.test(pwd),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
  };

  const passedChecks = Object.values(checks).filter(Boolean).length;

  let strength: 'weak' | 'fair' | 'good' | 'strong' = 'weak';
  let color = 'bg-destructive';

  if (passedChecks >= 5) {
    strength = 'strong';
    color = 'bg-green-500';
  } else if (passedChecks >= 4) {
    strength = 'good';
    color = 'bg-green-400';
  } else if (passedChecks >= 2) {
    strength = 'fair';
    color = 'bg-yellow-500';
  }

  return { checks, strength, color, percentage: (passedChecks / 5) * 100 };
};

interface AuthFormProps {
  defaultTab: 'signup' | 'signin';
  hasReferralDiscount: boolean;
  hasTrialOffer: boolean;
}

export const AuthForm = ({ defaultTab, hasReferralDiscount }: AuthFormProps) => {
  const { signIn, signUp } = useAuth();
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const passwordStrength = getPasswordStrength(password);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setError(null);

    const { error } = await signIn(email, password);
    if (error) setError(error.message);
    setAuthLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptedTerms) {
      setError('Please accept the Terms of Use and Privacy Policy to continue.');
      return;
    }

    if (isDisposableEmail(email)) {
      setError(DISPOSABLE_EMAIL_ERROR);
      return;
    }

    setAuthLoading(true);
    setError(null);

    const { error } = await signUp(email, password, fullName);
    if (error) {
      setError(error.message);
    } else if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'sign_up', {
        method: 'email',
        has_referral: hasReferralDiscount,
      });
    }
    // Email confirmation is disabled: a successful sign-up returns a session
    // and the page redirects to /dashboard, so no "check your email" notice.
    setAuthLoading(false);
  };

  const handleSocialLogin = async (provider: 'google' | 'apple') => {
    setAuthLoading(true);
    setError(null);

    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'sign_up', {
        method: provider,
        has_referral: hasReferralDiscount,
      });
    }

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });

    if (error) setError(error.message);
    setAuthLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setError(null);

    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      setError(error.message);
    } else {
      setError('Check your email for the password reset link!');
      setShowForgotPassword(false);
      setResetEmail('');
    }
    setAuthLoading(false);
  };

  return (
    <div className="auth-form">
      {hasReferralDiscount && (
        <Alert className="mb-6 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
          <Gift className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200 text-sm">
            🎉 <strong>10% OFF</strong> your first subscription!
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="auth-tabs">
          <TabsTrigger value="signup" className="auth-tab">Sign Up</TabsTrigger>
          <TabsTrigger value="signin" className="auth-tab">Sign In</TabsTrigger>
        </TabsList>

        {!isNative && (
          <div className="auth-social">
            <Button
              variant="outline"
              className="auth-social-btn"
              onClick={() => handleSocialLogin('google')}
              disabled={authLoading}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>

            <Button
              variant="outline"
              className="auth-social-btn"
              onClick={() => handleSocialLogin('apple')}
              disabled={authLoading}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              Continue with Apple
            </Button>

            <div className="auth-divider">
              <span>Or continue with email</span>
            </div>
          </div>
        )}

        <TabsContent value="signup" className="mt-0">
          <form onSubmit={handleSignUp} className="auth-fields">
            <div className="auth-field">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Your full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="auth-input"
              />
            </div>
            <div className="auth-field">
              <Label htmlFor="signupEmail">Email</Label>
              <Input
                id="signupEmail"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="auth-input"
              />
            </div>
            <div className="auth-field">
              <Label htmlFor="signupPassword">Password</Label>
              <Input
                id="signupPassword"
                type="password"
                placeholder="6+ characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="auth-input"
              />
              {password && (
                <div className="flex items-center gap-2 mt-2">
                  <Progress value={passwordStrength.percentage} className="h-1.5 flex-1" />
                  <span
                    className={`text-xs font-medium capitalize ${
                      passwordStrength.strength === 'strong'
                        ? 'text-green-500'
                        : passwordStrength.strength === 'good'
                        ? 'text-green-400'
                        : passwordStrength.strength === 'fair'
                        ? 'text-yellow-500'
                        : 'text-destructive'
                    }`}
                  >
                    {passwordStrength.strength}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked === true)}
                className="mt-0.5"
              />
              <Label htmlFor="terms" className="text-sm font-normal leading-tight cursor-pointer">
                I agree to the{' '}
                <a href="/terms" target="_blank" className="text-primary underline hover:no-underline">Terms of Use</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" className="text-primary underline hover:no-underline">Privacy Policy</a>
              </Label>
            </div>
            <Button type="submit" className="auth-submit" disabled={authLoading || !acceptedTerms}>
              {authLoading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="signin" className="mt-0">
          {!showForgotPassword ? (
            <form onSubmit={handleSignIn} className="auth-fields">
              <div className="auth-field">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="auth-input"
                />
              </div>
              <div className="auth-field">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="auth-input"
                />
              </div>
              <Button type="submit" className="auth-submit" disabled={authLoading}>
                {authLoading ? 'Signing in...' : 'Sign In'}
              </Button>
              <div className="text-center">
                <Button
                  variant="link"
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-muted-foreground hover:text-primary p-0 h-auto"
                >
                  Forgot password?
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword} className="auth-fields">
              <div className="auth-field">
                <Label htmlFor="resetEmail">Email</Label>
                <Input
                  id="resetEmail"
                  type="email"
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="auth-input"
                />
              </div>
              <Button type="submit" className="auth-submit" disabled={authLoading}>
                {authLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <div className="text-center">
                <Button
                  variant="link"
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                    setError(null);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary p-0 h-auto"
                >
                  Back to sign in
                </Button>
              </div>
            </form>
          )}
        </TabsContent>
      </Tabs>

      {error && (
        <Alert className="mt-4">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
